import React, { useState } from "react";
import { Container, Row, Col, Form, Button, Card, Alert } from "react-bootstrap";
import "../style/signup.css";
import { 
  INFO_STATEMENTS, 
  ERROR_MESSAGES,
  HTTP
} from '../util/constants.js'

const SignupForm = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    sex: '',
    dateOfBirth: '',
    idPassportNumber: '',
    phoneNumber: '',
    county: ''
  });

  // List of 47 counties in Kenya
  const kenyanCounties = [
    'Baringo', 'Bomet', 'Bungoma', 'Busia', 'Elgeyo-Marakwet', 'Embu', 'Garissa',
    'Homa Bay', 'Isiolo', 'Kajiado', 'Kakamega', 'Kericho', 'Kiambu', 'Kilifi',
    'Kirinyaga', 'Kisii', 'Kisumu', 'Kitui', 'Kwale', 'Laikipia', 'Lamu', 'Machakos',
    'Makueni', 'Mandera', 'Marsabit', 'Meru', 'Migori', 'Mombasa', 'Murang\'a',
    'Nairobi', 'Nakuru', 'Nandi', 'Narok', 'Nyamira', 'Nyandarua', 'Nyeri',
    'Samburu', 'Siaya', 'Taita-Taveta', 'Tana River', 'Tharaka-Nithi', 'Trans Nzoia',
    'Turkana', 'Uasin Gishu', 'Vihiga', 'Wajir', 'West Pokot'
  ];

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Phone number validation - only allow numbers and format
    if (name === 'phoneNumber') {
      const phoneRegex = /^[0-9+\-\s()]*$/;
      if (!phoneRegex.test(value)) {
        return; // Don't update if invalid characters
      }
    }
    
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const validatePhoneNumber = (phone) => {
    // Kenyan phone number validation
    const kenyanPhoneRegex = /^(\+254|0)[17]\d{8}$/;
    return kenyanPhoneRegex.test(phone.replace(/[\s\-()]/g, ''));
  };

  const calculateAge = (dateOfBirth) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    // Client-side validation
    if (formData.password !== formData.confirmPassword) {
      setError(ERROR_MESSAGES.PASSWORD_MISSMATCH);
      setIsLoading(false);
      return;
    }

    if (formData.password.length < 8) {
      setError(ERROR_MESSAGES.PASSWORD_LENGTH_ERROR);
      setIsLoading(false);
      return;
    }

    // Validate phone number
    if (!validatePhoneNumber(formData.phoneNumber)) {
      setError('Please enter a valid Kenyan phone number (e.g., +254712345678 or 0712345678)');
      setIsLoading(false);
      return;
    }

    // Validate age (must be 18 or older)
    if (formData.dateOfBirth) {
      const age = calculateAge(formData.dateOfBirth);
      if (age < 18) {
        setError('You must be at least 18 years old to create an account');
        setIsLoading(false);
        return;
      }
    }

    // Validate ID/Passport number
    if (formData.idPassportNumber.length < 6) {
      setError('Please enter a valid ID or Passport number');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_WENGMACK_V1_BE_URL}/signup`, {
        method: HTTP.METHODS.POST_METHOD,
        headers: {
          [HTTP.HEADERS.CONTENT_TYPE]: HTTP.HEADERS.APPLICATION_JSON_CONTENT_TYPE,
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          password_hash: formData.password,
          sex: formData.sex,
          date_of_birth: formData.dateOfBirth,
          id_number: formData.idPassportNumber,
          phone_number: formData.phoneNumber.replace(/[\s\-()]/g, ''), // Clean phone number
          county: formData.county,
          role: 'user' // Default role
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSuccess(INFO_STATEMENTS.ACCOUNT_CREATED);
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          password: '',
          confirmPassword: '',
          sex: '',
          dateOfBirth: '',
          idPassportNumber: '',
          phoneNumber: '',
          county: ''
        });
      } else {
        // Handle different error types
        if (data.error) {
          setError(data.error);
        } else if (data.message) {
          setError(data.message);
        } else {
          setError(ERROR_MESSAGES.ERROR_RESPONSE);
        }
      }
    } catch (error) {
      console.error('Signup error:', error);
      setError(ERROR_MESSAGES.NETWORK_ERROR);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section className="signupSection">
      <Container>
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            <Card className="shadow-lg border-0">
              <Card.Body className="p-5">
                <div className="text-center mb-4">
                  <h2 className="fw-bold text-secondary">Create Your Account</h2>
                  <p className="text-muted">Join Wengmack today and get started</p>
                </div>
                
                {error && (
                  <Alert variant="danger" className="mb-3">
                    {error}
                  </Alert>
                )}
                
                {success && (
                  <Alert variant="success" className="mb-3">
                    {success}
                  </Alert>
                )}

                <Form onSubmit={handleSubmit}>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="firstName"
                          value={formData.firstName}
                          onChange={handleChange}
                          placeholder="Enter your first name"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="lastName"
                          value={formData.lastName}
                          onChange={handleChange}
                          placeholder="Enter your last name"
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Email Address</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Enter your email"
                      required
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder="Create a password"
                      required
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Confirm Password</Form.Label>
                    <Form.Control
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm your password"
                      required
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Sex</Form.Label>
                        <Form.Select
                          name="sex"
                          value={formData.sex}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select your sex</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Date of Birth</Form.Label>
                        <Form.Control
                          type="date"
                          name="dateOfBirth"
                          value={formData.dateOfBirth}
                          onChange={handleChange}
                          max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                          required
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ID/Passport Number</Form.Label>
                        <Form.Control
                          type="text"
                          name="idPassportNumber"
                          value={formData.idPassportNumber}
                          onChange={handleChange}
                          placeholder="Enter your ID or Passport number"
                          minLength="6"
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Phone Number</Form.Label>
                        <Form.Control
                          type="tel"
                          name="phoneNumber"
                          value={formData.phoneNumber}
                          onChange={handleChange}
                          placeholder="e.g., +254712345678 or 0712345678"
                          required
                        />
                        <Form.Text className="text-muted">
                        </Form.Text>
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-4">
                    <Form.Label>County</Form.Label>
                    <Form.Select
                      name="county"
                      value={formData.county}
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select your county</option>
                      {kenyanCounties.map((county, index) => (
                        <option key={index} value={county}>
                          {county}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  
                  <div className="d-grid">
                    <Button 
                      variant="primary" 
                      type="submit" 
                      size="lg" 
                      disabled={isLoading}
                    >
                      {isLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Creating Account...
                        </>
                      ) : (
                        <>
                          Create Account <i className="bi bi-person-plus"></i>
                        </>
                      )}
                    </Button>
                  </div>
                  
                  <div className="text-center mt-3">
                    <p className="text-muted">
                      Already have an account? <a href="/login" className="text-primary">Sign in</a>
                    </p>
                  </div>
                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </section>
  );
};

export default SignupForm;
